import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { toast } from 'sonner';
import { formatPhone } from '../utils/formatPhone';
import {
  X, User, Phone, MapPin, Briefcase, Heart, Plane,
  Building2, CheckCircle, XCircle, Clock, Lock, ExternalLink, FileText
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Row = ({ label, value, mono = false }) => {
  if (!value && value !== 0) return null;
  return (
    <div className="flex gap-3 py-1.5 border-b border-slate-50 last:border-0">
      <span className="text-xs text-slate-400 w-36 flex-shrink-0 pt-0.5">{label}</span>
      <span className={`text-sm text-slate-800 flex-1 ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  );
};

const TaskRow = ({ done, label }) => (
  <div className="flex items-center gap-2 py-1">
    {done
      ? <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
      : <XCircle className="w-4 h-4 text-slate-300 flex-shrink-0" />}
    <span className={`text-sm ${done ? 'text-slate-700' : 'text-slate-400'}`}>{label}</span>
  </div>
);

const TASK_LABELS = {
  profile_complete:   'Complete Profile',
  waiver_signed:      'Sign Waiver',
  license_uploaded:   'Upload License',
  flight_submitted:   'Submit Flight Info',
  hotel_preferences:  'Hotel Preferences',
  room_assigned:      'Room Assigned',
  bag_assigned:       'Bag Assigned',
  donation_confirmed: 'Donation Confirmed',
};

const ParticipantProfileModal = ({ participant, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/admin/participants/${participant.user_id}/full-profile`)
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load profile'))
      .finally(() => setLoading(false));
  }, [participant.user_id]);

  const p    = data?.profile || {};
  const user = data?.user    || {};
  const name = p.first_name ? `${p.first_name} ${p.last_name || ''}`.trim() : user.name || 'Unknown';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-heading font-bold text-slate-800">{name}</h2>
              <p className="text-xs text-slate-500">{user.email} · {user.user_type === 'staff' ? 'Support Staff' : 'Trip Participant'}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            <Tabs defaultValue="profile" className="h-full">
              <div className="px-4 sm:px-6 pt-3 pb-0 border-b border-slate-100 overflow-x-auto scrollbar-thin">
                <TabsList className="w-max min-w-full justify-start rounded-none bg-transparent h-auto gap-1 pb-0">
                  <TabsTrigger value="profile" className="text-xs whitespace-nowrap">Profile</TabsTrigger>
                  <TabsTrigger value="travel" className="text-xs whitespace-nowrap">
                    <span className="sm:hidden">Travel</span>
                    <span className="hidden sm:inline">Flight & Hotel</span>
                    {data?.trips?.some(t => !t.tasks?.flight_submitted) && (
                      <span className="ml-1 w-2 h-2 rounded-full bg-amber-400 inline-block" />
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="health" className="text-xs whitespace-nowrap">
                    <span className="sm:hidden">Health</span>
                    <span className="hidden sm:inline">Health & Emergency</span>
                  </TabsTrigger>
                  <TabsTrigger value="tasks" className="text-xs whitespace-nowrap">Tasks</TabsTrigger>
                  <TabsTrigger value="credentials" className="text-xs whitespace-nowrap">
                    <span className="sm:hidden">Creds</span>
                    <span className="hidden sm:inline">Credentials</span>
                    <span className="ml-1">({data?.licenses?.length || 0})</span>
                  </TabsTrigger>
                  {data?.assignments?.length > 0 && (
                    <TabsTrigger value="assignments" className="text-xs whitespace-nowrap">
                      <span className="sm:hidden">Assign</span>
                      <span className="hidden sm:inline">Assignments</span>
                      <span className="ml-1">({data.assignments.filter(a => a.status !== 'completed').length})</span>
                    </TabsTrigger>
                  )}
                </TabsList>
              </div>

              {/* ── Profile tab ── */}
              <TabsContent value="profile" className="p-6 space-y-5">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5" />Personal Information
                    </p>
                    <Row label="First Name"    value={p.first_name} />
                    <Row label="Last Name"     value={p.last_name} />
                    <Row label="Email"         value={user.email} />
                    <Row label="Phone"         value={formatPhone(p.phone)} />
                    <Row label="Date of Birth" value={p.date_of_birth} />
                    <Row label="Organization"  value={p.organization} />
                    <Row label="Spanish Level" value={p.spanish_level} />
                    <Row label="Excursions"    value={p.interested_in_excursions ? 'Interested' : 'Not interested'} />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                      <MapPin className="w-3.5 h-3.5" />Address
                    </p>
                    <Row label="Street" value={p.street_address} />
                    <Row label="City"   value={p.city} />
                    <Row label="State"  value={p.state} />
                    <Row label="Zip"    value={p.zip_code} />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                      <Lock className="w-3.5 h-3.5" />Passport
                    </p>
                    <Row label="Surname" value={p.passport_surname} />
                    <Row label="Given Names" value={p.passport_given_names} />
                    <Row label="Passport Number"  value={p.passport_number} mono />
                    <Row label="Country"          value={p.passport_country} />
                    <Row label="Expiration" value={p.passport_expiration ? (() => {
                      const exp = new Date(p.passport_expiration);
                      const sixMonths = new Date(); sixMonths.setMonth(sixMonths.getMonth() + 6);
                      const isExpired = exp < new Date();
                      const isExpiring = !isExpired && exp < sixMonths;
                      return (
                        <span className={isExpired ? 'text-red-600 font-medium' : isExpiring ? 'text-amber-600 font-medium' : ''}>
                          {p.passport_expiration}{isExpired ? ' — EXPIRED' : isExpiring ? ' — Expiring soon' : ''}
                        </span>
                      );
                    })() : null} />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                      <Briefcase className="w-3.5 h-3.5" />Medical Role
                    </p>
                    {(p.work_roles || []).length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {(p.work_roles || []).map(r => (
                          <Badge key={r} variant="outline" className="text-xs">{r}</Badge>
                        ))}
                      </div>
                    ) : <p className="text-xs text-slate-400 italic">No roles on file</p>}
                    {p.glove_size && <div className="mt-2"><Row label="Glove Size" value={p.glove_size} /></div>}
                    {p.shirt_size && <div className="mt-1"><Row label="Shirt Size" value={p.shirt_size} /></div>}
                    {p.admin_notes && (
                      <div className="mt-3 p-3 bg-amber-50 rounded-lg text-xs text-amber-800 border border-amber-100">
                        <p className="font-medium mb-0.5">Admin Notes</p>
                        <p>{p.admin_notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* ── Flight & Hotel tab ── */}
              <TabsContent value="travel" className="p-6">
                {(data?.trips || []).length === 0 ? (
                  <p className="text-slate-400 text-sm">Not assigned to any trips</p>
                ) : (
                  <div className="space-y-6">
                    {(data?.trips || []).map(td => (
                      <div key={td.trip.trip_id} className="border border-slate-200 rounded-xl overflow-hidden">
                        <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                          <p className="font-semibold text-slate-800 text-sm">{td.trip.name}</p>
                          <p className="text-xs text-slate-500">{td.trip.start_date} – {td.trip.end_date}</p>
                        </div>
                        <div className="p-4 grid sm:grid-cols-2 gap-6">
                          <div>
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                              <Plane className="w-3.5 h-3.5" />Arrival Flight
                            </p>
                            {td.flight ? (
                              <>
                                <Row label="Date"     value={td.flight.arrival_date} />
                                <Row label="Time"     value={td.flight.arrival_time} />
                                <Row label="Airline"  value={td.flight.arrival_airline} />
                                <Row label="Flight #" value={td.flight.arrival_flight_number} mono />
                                <Row label="From"     value={td.flight.arrival_origin} />
                              </>
                            ) : <p className="text-xs text-slate-400 italic">Not submitted</p>}

                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 mt-4 flex items-center gap-1.5">
                              <Plane className="w-3.5 h-3.5 rotate-180" />Return Flight
                            </p>
                            {td.flight?.departure_date ? (
                              <>
                                <Row label="Date"     value={td.flight.departure_date} />
                                <Row label="Time"     value={td.flight.departure_time} />
                                <Row label="Airline"  value={td.flight.departure_airline} />
                                <Row label="Flight #" value={td.flight.departure_flight_number} mono />
                              </>
                            ) : <p className="text-xs text-slate-400 italic">Not submitted</p>}
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                              <Building2 className="w-3.5 h-3.5" />Hotel Preference
                            </p>
                            {td.hotel_preference?.roommate_preference ? (
                              <p className="text-sm text-slate-700 italic">"{td.hotel_preference.roommate_preference}"</p>
                            ) : <p className="text-xs text-slate-400 italic">No preference submitted</p>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* ── Health & Emergency tab ── */}
              <TabsContent value="health" className="p-6 space-y-5">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                      <Heart className="w-3.5 h-3.5" />Health
                    </p>
                    <Row label="Health Issues"  value={p.health_issues || 'None noted'} />
                    <Row label="Medications"    value={p.medications || 'None noted'} />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                      <Phone className="w-3.5 h-3.5" />Emergency Contact 1
                    </p>
                    <Row label="Name"  value={p.emergency_contact_1_name} />
                    <Row label="Phone" value={formatPhone(p.emergency_contact_1_phone)} />
                    <Row label="Email" value={p.emergency_contact_1_email} />

                    {(p.emergency_contact_2_name) && (
                      <>
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 mt-4 flex items-center gap-1.5">
                          <Phone className="w-3.5 h-3.5" />Emergency Contact 2
                        </p>
                        <Row label="Name"  value={p.emergency_contact_2_name} />
                        <Row label="Phone" value={formatPhone(p.emergency_contact_2_phone)} />
                        <Row label="Email" value={p.emergency_contact_2_email} />
                      </>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* ── Tasks tab ── */}
              <TabsContent value="tasks" className="p-6">
                {(data?.trips || []).length === 0 ? (
                  <p className="text-slate-400 text-sm">Not assigned to any trips</p>
                ) : (
                  <div className="space-y-5">
                    {/* Profile-level tasks */}
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Profile & Credentials</p>
                      <div className="grid sm:grid-cols-3 gap-1">
                        {['profile_complete', 'waiver_signed', 'license_uploaded'].map(key => (
                          <TaskRow key={key} done={!!data.trips[0]?.tasks?.[key]} label={TASK_LABELS[key]} />
                        ))}
                      </div>
                    </div>
                    {/* Per-trip tasks */}
                    {(data?.trips || []).map(td => (
                      <div key={td.trip.trip_id}>
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">{td.trip.name}</p>
                        <div className="grid sm:grid-cols-3 gap-1">
                          {['flight_submitted', 'hotel_preferences', 'room_assigned', 'bag_assigned', 'donation_confirmed'].map(key => (
                            <TaskRow key={key} done={!!td.tasks?.[key]} label={TASK_LABELS[key]} />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* ── Credentials tab ── */}
              <TabsContent value="credentials" className="p-6">
                {(data?.licenses || []).length === 0 ? (
                  <p className="text-slate-400 text-sm italic">No credentials uploaded</p>
                ) : (
                  <div className="space-y-3">
                    {(data?.licenses || []).map(lic => (
                      <div key={lic.license_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100">
                        <div className="flex items-center gap-3">
                          <FileText className="w-4 h-4 text-primary flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-slate-800">{lic.file_name}</p>
                            <p className="text-xs text-slate-500">{lic.license_type} · {new Date(lic.uploaded_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={
                            lic.status === 'verified' ? 'bg-green-100 text-green-700 border-0 text-xs' :
                            lic.status === 'rejected' ? 'bg-red-100 text-red-700 border-0 text-xs' :
                            'bg-yellow-100 text-yellow-700 border-0 text-xs'
                          }>{lic.status}</Badge>
                          <a href={`${BACKEND_URL}/api/licenses/${lic.license_id}/file`} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="w-4 h-4 text-slate-400 hover:text-primary" />
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* ── Assignments tab ── */}
              <TabsContent value="assignments" className="p-6">
                <div className="space-y-2">
                  {(data?.assignments || []).map(a => (
                    <div key={a.task_id} className={`flex items-start gap-3 p-3 rounded-xl border ${a.status === 'completed' ? 'bg-slate-50 border-slate-100 opacity-60' : 'bg-amber-50 border-amber-200'}`}>
                      {a.status === 'completed'
                        ? <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                        : <Clock className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />}
                      <div>
                        <p className={`text-sm font-medium ${a.status === 'completed' ? 'line-through text-slate-400' : 'text-slate-800'}`}>{a.title}</p>
                        {a.description && <p className="text-xs text-slate-500">{a.description}</p>}
                        {a.due_date && <p className="text-xs text-slate-400 mt-0.5">Due: {a.due_date}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParticipantProfileModal;
